import type { LucideIcon } from 'lucide-react';
import { BookOpen, FolderKanban, Gauge, Inbox, LayoutDashboard, MessagesSquare, PlayCircle, Server, ShieldAlert, Sparkles, Users } from 'lucide-react';
import { projectHomePath, projectPath, projectSlugFromPath } from '@/navigation/project-paths';
import { getCurrentProject } from '@/lib/current-project';
import { projectIconComponent } from '@/components/factory/project-icons';
import type { ProjectIconName } from '@shared/project-icon';

/** The product's name, in tab titles and the sidebar. */
export const PRODUCT_NAME = 'Software Factory';

/** Nested sidebar link under a parent NavItem (no icon). */
export interface NavChild {
  id: string;
  label: string;
  path: string;
  /**
   * Path prefixes that mark this child active (longest match wins).
   * Empty array = exact `path` only.
   */
  activeMatchPrefixes: string[];
  /** Hidden from people who aren't admins (the page shows "Only admins" for a direct link). */
  adminOnly?: boolean;
}

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** The icon's colour (a project's own colour, T-18). */
  iconColour?: string;
  /** Path prefixes that mark this item active (longest match wins). */
  activeMatchPrefixes: string[];
  adminOnly?: boolean;
  /** Optional expandable children (a project's pages). */
  children?: NavChild[];
  /** A count beside the label (the Inbox's unread items, T-37); nothing shows at 0. */
  count?: number;
}

type NavMatchable = {
  path: string;
  activeMatchPrefixes: string[];
};

/** A project the sidebar lists, with its pages. */
export interface NavProject {
  slug: string;
  name: string;
  /** Hex colour for its icon; left out, the icon keeps the sidebar's colour. */
  color?: string;
  /** Its icon (T-25); left out or null, the default folder. */
  icon?: ProjectIconName | null;
}

/** A project's sidebar item: its Board (its home, SF-T-189), Overview (SF-T-43), Runs and Settings, collapsible. */
function projectNavItem(project: NavProject): NavItem {
  const base = `/p/${project.slug}`;
  const id = `project:${project.slug}`;
  return {
    id,
    label: project.name,
    path: projectHomePath(project.slug),
    icon: projectIconComponent(project.icon),
    iconColour: project.color,
    activeMatchPrefixes: [base],
    children: [
      { id: `${id}:board`, label: 'Board', path: projectHomePath(project.slug), activeMatchPrefixes: [`${base}/board`] },
      { id: `${id}:overview`, label: 'Overview', path: projectPath(project.slug, 'overview'), activeMatchPrefixes: [`${base}/overview`] },
      { id: `${id}:runs`, label: 'Runs', path: projectPath(project.slug, 'runs'), activeMatchPrefixes: [`${base}/runs`] },
      {
        id: `${id}:settings`,
        label: 'Settings',
        path: projectPath(project.slug, 'settings'),
        activeMatchPrefixes: [`${base}/settings`],
      },
    ],
  };
}

/**
 * The sidebar (docs/first-slice-plan.md §6): Inbox (what's waiting on me, T-37; `inboxCount` its unread items), Board (every project's tasks, the home page; T-26), Chat (admins, T-51), Projects, then each project with its Board, Overview, Runs and
 * Settings (every project the caller can see, so each one expands in place), then Runs across
 * projects, Usage across projects, Fleet, AI, Agent config and People (admins).
 */
export function buildNavItems(projects: readonly NavProject[] = [], inboxCount = 0): NavItem[] {
  const items: NavItem[] = [
    { id: 'inbox', label: 'Inbox', path: '/inbox', icon: Inbox, activeMatchPrefixes: ['/inbox'], count: inboxCount },
    { id: 'board', label: 'Board', path: '/board', icon: LayoutDashboard, activeMatchPrefixes: ['/board'] },
    { id: 'chat', label: 'Chat', path: '/chat', icon: MessagesSquare, activeMatchPrefixes: ['/chat'], adminOnly: true },
    {
      id: 'projects',
      label: 'Projects',
      path: '/projects',
      icon: FolderKanban,
      activeMatchPrefixes: ['/projects'],
    },
    ...projects.map(projectNavItem),
  ];
  items.push(
    { id: 'runs', label: 'Runs', path: '/runs', icon: PlayCircle, activeMatchPrefixes: ['/runs'] },
    { id: 'usage', label: 'Usage', path: '/usage', icon: Gauge, activeMatchPrefixes: ['/usage'] },
    { id: 'fleet', label: 'Fleet', path: '/fleet', icon: Server, activeMatchPrefixes: ['/fleet'], adminOnly: true },
    { id: 'ai', label: 'AI', path: '/ai', icon: Sparkles, activeMatchPrefixes: ['/ai'], adminOnly: true },
    { id: 'agent-config', label: 'Agent config', path: '/agent-config', icon: BookOpen, activeMatchPrefixes: ['/agent-config'], adminOnly: true },
    { id: 'guard-catches', label: 'Guard catches', path: '/guard-catches', icon: ShieldAlert, activeMatchPrefixes: ['/guard-catches'], adminOnly: true },
    { id: 'people', label: 'People', path: '/admin/people', icon: Users, activeMatchPrefixes: ['/admin/people'], adminOnly: true }
  );
  return items;
}

/**
 * The sidebar for a path, for titles and breadcrumbs: the project's name when it's the current
 * project, else its slug.
 */
export function navItemsForPath(pathname: string): NavItem[] {
  const slug = projectSlugFromPath(pathname);
  if (!slug) return buildNavItems();
  const current = getCurrentProject();
  const isCurrent = current?.slug === slug;
  return buildNavItems([{ slug, name: isCurrent ? current.name : slug, color: isCurrent ? current.color : undefined }]);
}

/** Every top-level item outside a project (the default list, for tests and the gallery). */
export const NAV_ITEMS: NavItem[] = buildNavItems();

/** Root `/` only matches home; other prefixes use startsWith. */
export function matchesNavPrefix(pathname: string, prefix: string): boolean {
  if (prefix === '/') {
    return pathname === '/' || pathname === '/home';
  }
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function pathMatchesNavItem(pathname: string, item: NavMatchable): boolean {
  if (pathname === item.path) return true;
  return item.activeMatchPrefixes.some((prefix) => matchesNavPrefix(pathname, prefix));
}

function scoreNavMatch(pathname: string, item: NavMatchable): number {
  if (!pathMatchesNavItem(pathname, item)) return -1;

  let score = pathname === item.path ? item.path.length : 0;
  for (const prefix of item.activeMatchPrefixes) {
    if (matchesNavPrefix(pathname, prefix)) {
      score = Math.max(score, prefix.length);
    }
  }
  return score;
}

/**
 * Among matching items (and their children), prefer the longest matching prefix
 * so nested routes activate the most specific item. Children win over parents
 * when scores are equal.
 */
export function getActiveNavItemId(pathname: string, items: NavItem[]): string | null {
  let bestId: string | null = null;
  let bestScore = -1;

  const consider = (id: string, score: number, preferOnTie: boolean) => {
    if (score < 0) return;
    if (score > bestScore || (score === bestScore && preferOnTie)) {
      bestScore = score;
      bestId = id;
    }
  };

  for (const item of items) {
    if (item.children?.length) {
      for (const child of item.children) {
        consider(child.id, scoreNavMatch(pathname, child), true);
      }
      // Parent only if no child claimed the route (or tied with lower priority).
      consider(item.id, scoreNavMatch(pathname, item), false);
    } else {
      consider(item.id, scoreNavMatch(pathname, item), false);
    }
  }

  return bestId;
}

function findNavLabel(activeId: string, items: NavItem[]): string | undefined {
  for (const item of items) {
    if (item.id === activeId) return item.label;
    const child = item.children?.find((c) => c.id === activeId);
    if (child) return child.label;
  }
  return undefined;
}

/** Titles for pages that are not in the sidebar. */
const EXTRA_PAGE_TITLES: { prefix: string; title: string }[] = [
  { prefix: '/dev/ui', title: 'UI gallery' },
  { prefix: '/settings/api-keys', title: 'API keys' },
];

/** Section title for the sticky top bar, resolved from the current route. */
export function getPageTitle(pathname: string): string {
  for (const entry of EXTRA_PAGE_TITLES) {
    if (pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)) {
      return entry.title;
    }
  }
  const items = navItemsForPath(pathname);
  const activeId = getActiveNavItemId(pathname, items);
  if (!activeId) return '';
  return findNavLabel(activeId, items) ?? '';
}

/**
 * The browser tab's title, e.g. "Runs - Software Factory", so tabs can be told apart. Plain
 * "Software Factory" when the page has no name (sign-in). `recordTitle` (from PageHeader) goes in
 * front, so a record's tab names it.
 */
export function getDocumentTitle(pathname: string, recordTitle?: string | null): string {
  const page = getPageTitle(pathname);
  // A record page names the record first: "T-12 Fix the board · Board - Software Factory".
  const record = recordTitle?.trim();
  const name = record && record !== page ? (page ? `${record} · ${page}` : record) : page;
  return name ? `${name} - ${PRODUCT_NAME}` : PRODUCT_NAME;
}

export function filterNavItems(
  items: NavItem[],
  options: {
    isAdmin: boolean;
    searchQuery?: string;
  }
): NavItem[] {
  const query = options.searchQuery?.trim().toLowerCase() ?? '';

  return items.flatMap((original) => {
    if (original.adminOnly && !options.isAdmin) {
      return [];
    }
    const item = original.children?.some((child) => child.adminOnly)
      ? {
          ...original,
          children: original.children.filter((child) => !child.adminOnly || options.isAdmin),
        }
      : original;

    if (!query) {
      return [item];
    }

    const parentMatches = item.label.toLowerCase().includes(query) || item.path.toLowerCase().includes(query);
    if (parentMatches) {
      return [item];
    }

    if (item.children?.length) {
      const matchingChildren = item.children.filter(
        (child) => child.label.toLowerCase().includes(query) || child.path.toLowerCase().includes(query)
      );
      if (matchingChildren.length > 0) {
        return [{ ...item, children: matchingChildren }];
      }
    }

    return [];
  });
}

/** Flatten parents + visible children for sidebar search keyboard navigation. */
export function flattenNavTargets(items: NavItem[]): { id: string; path: string; label: string }[] {
  const targets: { id: string; path: string; label: string }[] = [];
  for (const item of items) {
    targets.push({ id: item.id, path: item.path, label: item.label });
    if (item.children?.length) {
      for (const child of item.children) {
        targets.push({ id: child.id, path: child.path, label: child.label });
      }
    }
  }
  return targets;
}
